import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { SocialAuthButtons } from "@/components/auth/SocialAuthButtons";

interface RegisterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RegisterDialog({ open, onOpenChange }: RegisterDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = {
        full_name: formData.fullName,
        user_type: "candidate",
      };

      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/login`,
        },
      });

      if (error) throw error;

      // Trigger handle_new_user já cria o profile automaticamente

      if (data) {
        try {
          const { data: brevoData, error: brevoError } =
            await supabase.functions.invoke("create-brevo-contact", {
              body: {
                email: formData.email,
                firstName: formData.fullName,
                lastName: "",
                userId: data.user?.id,
                userType: "candidate",
              },
            });

          if (brevoError) {
            console.error("Brevo API error:", brevoError);
          } else {
            console.log("Brevo contact created successfully:", brevoData);
          }
        } catch (brevoError) {
          console.error("Failed to create Brevo contact:", brevoError);
        }

        setRegisteredEmail(formData.email);
        setShowEmailModal(true);
      }
    } catch (error: unknown) {
      toast({
        title: "Erro no cadastro",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">
              Cadastre-se Grátis
            </DialogTitle>
            <DialogDescription className="text-center">
              Comece sua jornada em 30 segundos
            </DialogDescription>
          </DialogHeader>

          <SocialAuthButtons />

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dialog-fullName">Nome completo</Label>
              <Input
                id="dialog-fullName"
                value={formData.fullName}
                onChange={(e) =>
                  setFormData({ ...formData, fullName: e.target.value })
                }
                placeholder="Seu nome completo"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dialog-email">E-mail</Label>
              <Input
                id="dialog-email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="seu@email.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dialog-password">Senha</Label>
              <Input
                id="dialog-password"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                placeholder="••••••••"
                required
              />
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="dialog-terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                required
              />
              <label htmlFor="dialog-terms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                Li e concordo com os{" "}
                <Link to="/termos" className="text-primary hover:underline" target="_blank">
                  Termos de Uso
                </Link>{" "}
                e a{" "}
                <Link to="/politica-de-privacidade" className="text-primary hover:underline" target="_blank">
                  Política de Privacidade
                </Link>
              </label>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !acceptedTerms}
              size="lg"
            >
              {loading ? "Cadastrando..." : "Cadastrar Grátis"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showEmailModal}
        onOpenChange={() => {
          setShowEmailModal(false);
          onOpenChange(false);
        }}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-primary" />
              </div>
            </div>
            <AlertDialogTitle className="text-xl">
              📧 Verifique seu Email!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>Enviamos um email de confirmação para:</p>
              <div className="bg-muted p-3 rounded-lg text-center">
                <strong className="text-primary">{registeredEmail}</strong>
              </div>
              <div className="space-y-2">
                <p className="font-medium">Para completar seu cadastro:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Abra seu email</li>
                  <li>Clique no link de confirmação</li>
                  <li>Volte aqui e faça login</li>
                </ul>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                <p className="text-sm">
                  <strong>💡 Dica:</strong> Não esquece de verificar a caixa de
                  spam!
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogAction
            onClick={() => {
              setShowEmailModal(false);
              onOpenChange(false);
            }}
            className="w-full"
          >
            Entendi
          </AlertDialogAction>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
